import React, { useState, useContext } from "react";
import { format } from "date-fns";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import classNames from "classnames";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import IconButton from "@material-ui/core/IconButton";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";

import { UserContext, isAdministrator } from "../../context/user";
import { COUNTRIES, FORM_CONFIG, USA_STATES } from "../ApplicationForm/constants";
import { ApplicationForm } from "../ApplicationForm/ApplicationForm";
import { createApplications } from "../../api/applications";
import cn from "./styles.module.scss";


const initialForm = {
  [FORM_CONFIG.member_country.key]: COUNTRIES.usa.value,
  [FORM_CONFIG.member_state_or_province.key]: USA_STATES[0],
  [FORM_CONFIG.member_is_different_billing_address.key]: false,
  [FORM_CONFIG.member_billing_country.key]: COUNTRIES.usa.value,
  [FORM_CONFIG.member_billing_state_or_province.key]:  USA_STATES[0],
};

const validate = (form, config) => {
  const errors = Object.values(config).filter((value) => value.required).reduce((result, config) => {
    const { key } = config;
    if (!form[key]) {
      return { ...result, [key]: 'Required' }
    }
    return result;
  }, {});

  // validate billing address fields if custom billing address selected
  if (form[FORM_CONFIG.member_is_different_billing_address.key]) {
    [
      FORM_CONFIG.member_billing_country.key,
      FORM_CONFIG.member_billing_state_or_province.key,
      FORM_CONFIG.member_billing_address.key,
      FORM_CONFIG.member_billing_city.key,
      FORM_CONFIG.member_billing_zipcode.key
    ].forEach((key) => {
      if(!form[key]) {
        errors[key] = 'Required'
      }
    })
  }

  return { errors, valid: Object.keys(errors).length === 0 }
};

export const ApplyOnlineForm = ({ agencyNonce }) => {
  const user = useContext(UserContext);
  const [applications, setApplications] = useState([{ form: initialForm, errors: {} }]);
  const [memberAlreadyPaid, setMemberAlreadyPaid] = useState(false);

  const handleAddApplication = () => {
    setApplications([...applications, { form: initialForm, errors: {} }]);
  };

  const handleApplicationRemove = (application) => {
    setApplications(applications.filter((form) => form !== application))
  };

  const handleApplicationChange = (newApplication, oldApplication) => {
    setApplications(applications.map(application => application === oldApplication ? newApplication : application))
  };

  const validateApplications = () => {
    let isFormValid = true;
    const newApplications = applications.map((application) => {
      const { valid, errors } = validate(application.form, FORM_CONFIG);
      if (!valid) {
        isFormValid = false;
        return { ...application, errors }
      }
      return application;
    });
    setApplications(newApplications);
    return isFormValid;
  };

  const handleRegister = async () => {
    if (!validateApplications()) {
      return;
    }
    const { next_url } = await createApplications({
      applications: applications.map(({ form }) => ({
        ...form,
        [FORM_CONFIG.member_dob.key]: format(form[FORM_CONFIG.member_dob.key], 'MM/dd/yyyy'),
        [FORM_CONFIG.agency_nonce.key]: agencyNonce,
      })),
      [FORM_CONFIG.member_already_paid.key]: memberAlreadyPaid,
    });
    window.location.replace(next_url);
  };

  return (
    <div>
        <div className={cn.applicationsList}>
          {applications.map((application, index) => (
            <div className={cn.expandablePanelWrapper} key={index}>
              <ExpansionPanel
                defaultExpanded={index === 0}
                classes={{
                  root: classNames(
                    cn.expandablePanel,
                    { [cn.expandablePanelError]:
                      Object.values(application.errors).filter(err => !!err).length > 0
                    }
                  )
                }}
              >
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                  {
                    application.form[FORM_CONFIG.member_firstname.key] || application.form[FORM_CONFIG.member_lastname.key]
                      ? `${application.form[FORM_CONFIG.member_firstname.key] || ''} ${application.form[FORM_CONFIG.member_lastname.key] || ''}`
                      : `Application ${index + 1}`
                  }
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                  <ApplicationForm
                    agencyNonce={agencyNonce}
                    value={application}
                    onChange={(newApplication) => handleApplicationChange(newApplication, application)}
                  />
                </ExpansionPanelDetails>
              </ExpansionPanel>
              {index !== 0 && (
                <IconButton onClick={() => handleApplicationRemove(application)} className={cn.removeIcon}>
                  <RemoveCircleIcon color="primary" />
                </IconButton>
              )}
            </div>
          ))}
        </div>
        <div className={cn.footer}>
          <div className={cn.register}>
            {isAdministrator(user) &&
              <FormControlLabel
                  control={<Checkbox color="primary"/>}
                  label="Member already paid"
                  onChange={(event, checked) => setMemberAlreadyPaid(checked)}
                  classes={{root: cn.memberPaid}}
              />
            }
            <Button onClick={handleRegister} variant="contained" color="primary">
              Register
            </Button>
          </div>
          <Button onClick={handleAddApplication} variant="outlined" color="primary">
            Add Application Form
          </Button>
        </div>
    </div>
  );
};
